/** DRW-FIX-3 — who the signed-in person is, and whether they may remove a markup.
 *
 *  One predicate, no dependencies, so it can be checked without a browser
 *  (`node scripts/markup_delete_check.mjs`) and cannot drift between the office and field viewers.
 *
 *  The rule is the server's, verbatim from `backend/drawings/routes.py:374`: the AUTHOR, anyone in
 *  the office, or a Foreman. What was wrong before was not the rule but the reading of it — the
 *  client compared `principal.email`, and `/api/me` nests the person under `user`, so the author
 *  test matched `""` for everybody and the button only survived on a second, unrelated allow-list.
 */

/** The cached principal (`/api/me`), the same object the app keeps for working offline. */
export function signedIn() {
  try { return JSON.parse(localStorage.getItem("auth_principal") || "null") || {}; } catch { return {}; }
}

export const emailOf = (p = signedIn()) => (p.user?.email || p.email || "").toLowerCase();

// The host role → field role table, mirroring backend/fieldops/routes.py:50 (FIELD_ROLE_MAP) and
// drawings/store.py (FIELD_LAYER_ROLES). An unknown role is a Foreman, exactly as the server
// defaults. Keep the two in step — or better, have /api/me carry `field_role` and delete this half.
const FIELD_ROLE_BY_HOST_ROLE = {
  admin: "Admin", account_manager: "Admin", pm: "Scheduler", estimator: "Scheduler",
  project_engineer: "Scheduler", foreman: "Foreman", warehouse: "Warehouse",
  safety_director: "Safety",
};
const FIELD_LAYER_ROLES = ["Foreman", "Installer", "Warehouse"];

export const fieldRoleOf = (p = signedIn()) =>
  FIELD_ROLE_BY_HOST_ROLE[String(p.role || "").toLowerCase()] || "Foreman";
export const isFieldUser = (p = signedIn()) => FIELD_LAYER_ROLES.includes(fieldRoleOf(p));

/** The author, anyone in the office, or a Foreman — and never a Bluebeam markup. Nothing the
 *  server would refuse is offered; nothing it allows is hidden. */
export function canDeleteMarkup(m) {
  if (m?.origin === "ingest") return false;          // Bluebeam markups are not deleted here
  if (!m?.author_email) return true;                 // not yet synced — it is ours by definition
  const p = signedIn();
  return m.author_email.toLowerCase() === emailOf(p) || !isFieldUser(p) || fieldRoleOf(p) === "Foreman";
}
